import { IExpenseStorageService } from './IExpenseStorageService';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';

// Days of month a recurring expense occurs on. Falls back to the start
// date's own day for expenses recorded before multi-day recurrence existed.
function getRecurringDays(expense) {
  if (Array.isArray(expense.recurringDays) && expense.recurringDays.length > 0) {
    return expense.recurringDays;
  }
  return [new Date(expense.date + 'T00:00:00').getDate()];
}

// Projects a recurring expense into a specific year/month, returning one
// instance per configured day, each clamped to the last day of that month
// (e.g. a 31st occurrence projects to the 30th in a 30-day month).
function projectRecurringInstances(expense, year, month) {
  const lastDay = new Date(year, month, 0).getDate();
  const paddedMonth = String(month).padStart(2, '0');
  return getRecurringDays(expense).map(day => {
    const clampedDay = Math.min(day, lastDay);
    const paddedDay = String(clampedDay).padStart(2, '0');
    return { ...expense, date: `${year}-${paddedMonth}-${paddedDay}` };
  });
}

function incomeKey(year, month) {
  return `${year}-${String(month).padStart(2, '0')}`;
}

function generateId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Firebase Firestore implementation of IExpenseStorageService
 */
export class FirebaseExpenseService extends IExpenseStorageService {
  /**
   * @param {string} userId - Firebase user ID
   */
  constructor(userId) {
    super();
    if (!userId) {
      throw new Error('userId is required for FirebaseExpenseService');
    }
    this.userId = userId;
    this.expensesDocRef = doc(db, 'users', userId, 'data', 'expenses');
  }

  async _loadRaw() {
    try {
      const snap = await getDoc(this.expensesDocRef);
      return snap.exists()
        ? { expenses: snap.data().expenses || [], income: snap.data().income || {} }
        : { expenses: [], income: {} };
    } catch (error) {
      console.error('Error loading expenses from Firebase:', error);
      throw error;
    }
  }

  async _saveRaw(data) {
    try {
      await setDoc(this.expensesDocRef, data);
    } catch (error) {
      console.error('Error saving expenses to Firebase:', error);
      throw error;
    }
  }

  async getExpensesForMonth(year, month) {
    const { expenses } = await this._loadRaw();
    const result = [];

    for (const expense of expenses) {
      const expDate = new Date(expense.date + 'T00:00:00');
      const expYear = expDate.getFullYear();
      const expMonth = expDate.getMonth() + 1;

      if (!expense.recurring) {
        if (expYear === year && expMonth === month) {
          result.push(expense);
        }
      } else {
        const startedBefore = expYear < year || (expYear === year && expMonth <= month);
        if (startedBefore) {
          result.push(...projectRecurringInstances(expense, year, month));
        }
      }
    }

    return result.sort((a, b) => a.date.localeCompare(b.date));
  }

  async getExpensesForYear(year) {
    const { expenses } = await this._loadRaw();
    const result = [];

    for (const expense of expenses) {
      const expDate = new Date(expense.date + 'T00:00:00');
      const expYear = expDate.getFullYear();

      if (!expense.recurring) {
        if (expYear === year) {
          result.push(expense);
        }
      } else {
        const expMonth = expDate.getMonth() + 1;
        for (let month = 1; month <= 12; month++) {
          const startedBefore = expYear < year || (expYear === year && expMonth <= month);
          if (startedBefore) {
            result.push(...projectRecurringInstances(expense, year, month));
          }
        }
      }
    }

    return result.sort((a, b) => a.date.localeCompare(b.date));
  }

  async addExpense(data) {
    const raw = await this._loadRaw();
    const expense = { ...data, id: generateId() };
    raw.expenses.push(expense);
    await this._saveRaw(raw);
    return expense;
  }

  async updateExpense(expense) {
    const raw = await this._loadRaw();
    const idx = raw.expenses.findIndex(e => e.id === expense.id);
    if (idx === -1) throw new Error(`Expense ${expense.id} not found`);
    raw.expenses[idx] = expense;
    await this._saveRaw(raw);
    return expense;
  }

  async deleteExpense(id) {
    const raw = await this._loadRaw();
    raw.expenses = raw.expenses.filter(e => e.id !== id);
    await this._saveRaw(raw);
  }

  async getIncome(year, month) {
    const { income } = await this._loadRaw();
    // Walk backwards up to 24 months to find the most recent set value
    let y = year, m = month;
    for (let i = 0; i < 24; i++) {
      const val = income[incomeKey(y, m)];
      if (val !== undefined) return val;
      m--;
      if (m === 0) { m = 12; y--; }
    }
    return 0;
  }

  async setIncome(year, month, amount) {
    const raw = await this._loadRaw();
    raw.income[incomeKey(year, month)] = amount;
    await this._saveRaw(raw);
  }

  async exportData() {
    return this._loadRaw();
  }

  async importData(data) {
    await this._saveRaw({ expenses: data.expenses, income: data.income });
  }
}
